import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { Plus, Trash, Lightning, PencilSimple, EyeSlash, Eye, X, Lightbulb, ChatCircle, CalendarPlus, Check, CaretLeft, CaretRight, FileArrowUp } from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmProvider";
import { normalizePastedText, onPasteNormalize } from "@/pages/PlannerPage";
import BulkImportModal from "@/components/BulkImportModal";

const KIND_META = {
  pov: { label: "PoV / Thought", short: "PoV", Icon: ChatCircle },
  idea: { label: "Post Idea", short: "Idea", Icon: Lightbulb },
};

const normalizeKind = (k) => (k === "idea" ? "idea" : "pov");

const POST_TYPE_OPTIONS = ["Attract", "Nurture", "Position", "Convert"];
const POST_TYPE_STYLE = {
  attract:  { bg: "#FEE3D8", fg: "#B14818" },
  nurture:  { bg: "#E4EFDD", fg: "#3C6B23" },
  position: { bg: "#E3E7F5", fg: "#334095" },
  convert:  { bg: "#F7DFEB", fg: "#8A2E63" },
};
const postTypeStyle = (t) => {
  const k = (t || "").toLowerCase();
  const c = POST_TYPE_STYLE[k];
  if (c) return { background: c.bg, color: c.fg };
  return { background: "var(--paper)", color: "var(--ink-2)" };
};

export default function PovsPage() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ content: "", source: "", tags: [], active: true, kind: "pov", post_type_tag: "" });
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [filter, setFilter] = useState("all"); // all | pov | idea
  const [viewing, setViewing] = useState(null); // full-content read modal
  const [detailDraft, setDetailDraft] = useState(null); // {content, post_type_tag, active} — inline-edit buffer in detail modal
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null); // { tag, reason } — AI suggestion, distinct from committed form.post_type_tag
  const suggestTimer = useRef(null);
  const lastSuggested = useRef("");
  const [showBulk, setShowBulk] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/povs");
      setItems(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm({ content: "", source: "", tags: [], active: true, kind: "pov", post_type_tag: "" }); setEditingId(null); setSuggestion(null); lastSuggested.current = ""; setShowAdd(true); };
  const openEdit = (p) => { setForm({ content: p.content || "", source: p.source || "", tags: p.tags || [], active: p.active !== false, kind: normalizeKind(p.kind), post_type_tag: p.post_type_tag || "" }); setEditingId(p.id); setSuggestion(null); lastSuggested.current = p.content || ""; setShowAdd(true); };

  // Auto-suggest a post-type tag once the user has written enough content. Debounced.
  useEffect(() => {
    if (!showAdd) return;
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    const text = (form.content || "").trim();
    // Only suggest if user hasn't picked a tag yet, content is meaningful, and this text is new
    if (form.post_type_tag) { setSuggestion(null); return; }
    if (text.length < 30) { setSuggestion(null); return; }
    if (text === lastSuggested.current) return;
    suggestTimer.current = setTimeout(async () => {
      try {
        setSuggesting(true);
        const r = await api.post("/posts/classify-type", { text });
        lastSuggested.current = text;
        if (r.data?.tag) {
          setSuggestion({ tag: r.data.tag, reason: r.data.reason || "" });
        } else {
          setSuggestion(null);
        }
      } catch { setSuggestion(null); }
      finally { setSuggesting(false); }
    }, 900);
    return () => { if (suggestTimer.current) clearTimeout(suggestTimer.current); };
  }, [form.content, form.post_type_tag, showAdd]);

  const addTag = () => {
    const t = tagInput.trim().replace(/^#/, "");
    if (t && !form.tags.includes(t)) setForm({ ...form, tags: [...form.tags, t] });
    setTagInput("");
  };
  const removeTag = (t) => setForm({ ...form, tags: form.tags.filter(x => x !== t) });

  const save = async (e) => {
    e.preventDefault();
    const cleanedContent = normalizePastedText(form.content).trim();
    if (!cleanedContent) return;
    setSaving(true);
    try {
      const payload = { ...form, content: cleanedContent };
      if (editingId) {
        await api.patch(`/povs/${editingId}`, payload);
        toast.success(payload.kind === "idea" ? "Post idea updated." : "PoV updated. She's relearning.");
      } else {
        await api.post("/povs", payload);
        toast.success(payload.kind === "idea" ? "Post idea saved. She'll surface it when you're drafting." : "PoV captured. She'll pull it in when relevant.");
      }
      setShowAdd(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/povs/${p.id}`, { active: !(p.active !== false) });
      toast.success(p.active !== false ? "Muted from drafts." : "Re-activated.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (p) => {
    const label = normalizeKind(p.kind) === "idea" ? "post idea" : "PoV";
    const ok = await confirm({
      title: `Delete this ${label}?`,
      description: `"${(p.content || '').slice(0, 90)}${(p.content||'').length>90?'…':''}" will be moved to Trash. You can restore it anytime.`,
      confirmText: "Move to Trash",
    });
    if (!ok) return;
    await api.delete(`/povs/${p.id}`);
    toast.success("Moved to Trash.");
    load();
  };

  const sendToPlanner = async (p) => {
    try {
      const r = await api.post(`/povs/${p.id}/to-planner`);
      const alreadyLinked = r.data.already_linked;
      // Reflect the link on the local list so the button flips to "In Planner" immediately.
      setItems((list) => list.map(x => x.id === p.id ? { ...x, planner_post_id: r.data.post?.id } : x));
      setViewing((v) => (v && v.id === p.id ? { ...v, planner_post_id: r.data.post?.id } : v));
      toast.success(alreadyLinked ? "Already in your Planner backlog." : "Sent to Planner backlog.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const povCount = items.filter(p => normalizeKind(p.kind) === "pov").length;
  const ideaCount = items.filter(p => normalizeKind(p.kind) === "idea").length;
  const activeCount = items.filter(p => p.active !== false).length;

  const visible = useMemo(() => {
    if (filter === "all") return items;
    return items.filter(p => normalizeKind(p.kind) === filter);
  }, [items, filter]);

  // Prev/Next navigation inside the detail modal, over the current filtered list
  const viewIdx = viewing ? visible.findIndex(x => x.id === viewing.id) : -1;
  const canPrev = viewIdx > 0;
  const canNext = viewIdx >= 0 && viewIdx < visible.length - 1;
  const goPrev = () => { if (canPrev) setViewing(visible[viewIdx - 1]); };
  const goNext = () => { if (canNext) setViewing(visible[viewIdx + 1]); };

  // Reset the inline edit buffer whenever the viewed PoV changes
  useEffect(() => {
    if (viewing) {
      setDetailDraft({
        content: viewing.content || "",
        post_type_tag: viewing.post_type_tag || "",
        active: viewing.active !== false,
      });
    } else {
      setDetailDraft(null);
    }
  }, [viewing?.id]);

  // Keyboard shortcuts for prev/next inside the modal (skips when a form field is focused)
  useEffect(() => {
    if (!viewing) return;
    const onKey = (e) => {
      const t = e.target;
      const isFormField = t && (t.tagName === "TEXTAREA" || t.tagName === "INPUT" || t.isContentEditable);
      if (isFormField) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); goNext(); }
      else if (e.key === "Escape") { setViewing(null); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewing?.id, canPrev, canNext]);

  const detailDirty = detailDraft && viewing && (
    detailDraft.content !== (viewing.content || "") ||
    (detailDraft.post_type_tag || "") !== (viewing.post_type_tag || "") ||
    detailDraft.active !== (viewing.active !== false)
  );

  const saveDetail = async () => {
    if (!viewing || !detailDraft) return;
    const cleaned = normalizePastedText(detailDraft.content || "").trim();
    if (!cleaned) { toast.error("Content can't be empty."); return; }
    try {
      const payload = {
        content: cleaned,
        post_type_tag: detailDraft.post_type_tag || "",
        active: !!detailDraft.active,
      };
      await api.patch(`/povs/${viewing.id}`, payload);
      setItems(list => list.map(x => x.id === viewing.id ? { ...x, ...payload } : x));
      setViewing(v => v ? { ...v, ...payload } : v);
      toast.success("Saved.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const FilterChip = ({ value, count, label }) => (
    <button
      type="button"
      data-testid={`pov-filter-${value}`}
      onClick={() => setFilter(value)}
      className={`chip ${filter === value ? "chip-accent" : ""}`}
      style={filter === value ? {} : { cursor: 'pointer' }}
    >
      {label} <span className="opacity-60 ml-1">{count}</span>
    </button>
  );

  return (
    <div>
      <PageHeader
        eyebrow="PoV & Ideas"
        title="Shower thoughts, hot takes, and post ideas."
        subtitle="Capture your one-liners the moment they land, and jot down post ideas for later. She'll pull the relevant ones into drafts on her own. Mute anything you don't want her using yet."
        action={
          <div className="flex items-center gap-2">
            <button data-testid="pov-bulk-import" className="btn btn-ghost hidden sm:inline-flex" onClick={()=>setShowBulk(true)}><FileArrowUp size={14}/> Import</button>
            <button data-testid="pov-add-btn" className="btn btn-accent hidden sm:inline-flex" onClick={openAdd}><Plus size={16}/> Capture</button>
          </div>
        }
      />

      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-6">
        <div className="card p-3 sm:p-5 fade-up">
          <div className="eyebrow mb-2 flex items-center gap-1.5"><ChatCircle size={11} weight="fill" style={{color:'var(--accent)'}}/> <span className="hidden sm:inline">PoVs / thoughts</span><span className="sm:hidden">PoVs</span></div>
          <div className="font-serif text-2xl sm:text-3xl tracking-tight font-semibold">{povCount}</div>
        </div>
        <div className="card p-3 sm:p-5 fade-up">
          <div className="eyebrow mb-2 flex items-center gap-1.5"><Lightbulb size={11} weight="fill" style={{color:'var(--accent)'}}/> <span className="hidden sm:inline">Post ideas</span><span className="sm:hidden">Ideas</span></div>
          <div className="font-serif text-2xl sm:text-3xl tracking-tight font-semibold">{ideaCount}</div>
        </div>
        <div className="card p-3 sm:p-5 fade-up">
          <div className="eyebrow mb-2 flex items-center gap-1.5"><Lightning size={11} weight="fill" style={{color:'var(--accent)'}}/> <span className="hidden sm:inline">Active in drafts</span><span className="sm:hidden">Active</span></div>
          <div className="font-serif text-2xl sm:text-3xl tracking-tight font-semibold">{activeCount}</div>
        </div>
      </div>

      {items.length > 0 && (
        <div className="flex items-center gap-2 mb-4" data-testid="pov-filters">
          <FilterChip value="all" count={items.length} label="All" />
          <FilterChip value="pov" count={povCount} label="PoVs" />
          <FilterChip value="idea" count={ideaCount} label="Post ideas" />
        </div>
      )}

      {loading ? (
        <div className="text-sm" style={{ color: "var(--ink-3)" }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="card p-16 text-center">
          <Lightning size={26} className="mx-auto mb-3" style={{ color: "var(--accent)" }} weight="fill"/>
          <div className="eyebrow mb-2">Nothing yet</div>
          <p className="font-serif text-3xl mb-3">A great post starts as a bad idea.</p>
          <p className="text-sm max-w-md mx-auto" style={{ color: "var(--ink-2)" }}>
            Capture ideas the moment they hit. In the shower, in Slack, in an argument. She'll grab the relevant ones next time you're drafting.
          </p>
          <button data-testid="pov-empty-add" onClick={openAdd} className="btn btn-accent mt-6"><Plus size={16}/> Capture your first one</button>
        </div>
      ) : visible.length === 0 ? (
        <div className="card p-10 text-center text-sm" style={{ color: "var(--ink-2)" }}>
          Nothing here yet. Try switching filters or capture a new one.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="pov-list">
          {visible.map((p) => {
            const k = normalizeKind(p.kind);
            const meta = KIND_META[k];
            const KIcon = meta.Icon;
            const PREVIEW_LEN = 140;
            const truncated = (p.content || "").length > PREVIEW_LEN;
            const preview = truncated ? `${p.content.slice(0, PREVIEW_LEN).trimEnd()}…` : p.content;
            return (
              <button
                key={p.id}
                type="button"
                data-testid={`pov-${p.id}`}
                onClick={() => setViewing(p)}
                className="card p-5 fade-up flex flex-col text-left transition-all hover:shadow-md"
                style={p.active === false ? { opacity: 0.55 } : {}}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="chip text-xs flex items-center gap-1" data-testid={`pov-kind-${p.id}`}>
                      <KIcon size={11} weight="fill" style={{color:'var(--accent)'}}/> {meta.short}
                    </span>
                    {p.post_type_tag && (
                      <span className="chip text-xs" style={{padding:'2px 8px', ...postTypeStyle(p.post_type_tag)}} data-testid={`pov-post-type-tag-${p.id}`}>
                        {p.post_type_tag}
                      </span>
                    )}
                  </div>
                  <span className="font-mono text-xs" style={{color:'var(--ink-3)'}}>{new Date(p.created_at).toLocaleDateString(undefined,{month:'short',day:'numeric'})}</span>
                </div>
                <div className="text-sm leading-snug mb-3 flex-1 whitespace-pre-wrap" style={p.active === false ? { textDecoration: 'line-through', textDecorationColor: 'var(--ink-4)' } : {}}>
                  {preview}
                </div>
                {truncated && (
                  <div className="text-xs mb-2" style={{color:'var(--accent)'}}>Read more →</div>
                )}
                <div className="flex items-center gap-1.5 flex-wrap">
                  {(p.tags || []).slice(0, 3).map(t => <span key={t} className="chip text-xs">#{t}</span>)}
                  {(p.tags || []).length > 3 && <span className="text-xs" style={{color:'var(--ink-3)'}}>+{p.tags.length - 3}</span>}
                  {p.source && <span className="text-xs" style={{color:'var(--ink-3)'}}>from {p.source}</span>}
                </div>
                <div className="mt-3 pt-3 flex items-center justify-end" style={{borderTop:'1px dashed var(--line)'}}>
                  {p.planner_post_id ? (
                    <span
                      data-testid={`pov-in-planner-${p.id}`}
                      className="chip text-xs flex items-center gap-1"
                      style={{background:'var(--accent-tint)', color:'var(--accent-2)', border:'1px solid var(--accent)'}}
                      title="Already in your Planner backlog"
                    ><Check size={11} weight="bold"/> In Planner</span>
                  ) : (
                    <button
                      type="button"
                      data-testid={`pov-to-planner-${p.id}`}
                      onClick={(e) => { e.stopPropagation(); sendToPlanner(p); }}
                      className="chip text-xs flex items-center gap-1 cursor-pointer"
                      title="Add this to your Planner backlog as a post idea (stays here too)"
                    ><CalendarPlus size={11}/> Send to Planner</button>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {viewing && detailDraft && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-start justify-center sm:pt-16 overflow-y-auto" style={{background:'rgba(0,0,0,0.55)'}} onClick={()=>setViewing(null)}>
          <div onClick={(e)=>e.stopPropagation()} className="w-full sm:max-w-2xl card p-5 sm:p-6 fade-up rounded-b-none sm:rounded-b-3xl" style={{maxHeight:'92vh', overflowY:'auto'}} data-testid="pov-detail-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                {(() => {
                  const k = normalizeKind(viewing.kind);
                  const meta = KIND_META[k];
                  const KIcon = meta.Icon;
                  return (
                    <span className="chip text-xs flex items-center gap-1">
                      <KIcon size={11} weight="fill" style={{color:'var(--accent)'}}/> {meta.label}
                    </span>
                  );
                })()}
                {viewing.post_type_tag && (
                  <span className="chip text-xs" style={{padding:'2px 8px', ...postTypeStyle(viewing.post_type_tag)}}>
                    {viewing.post_type_tag}
                  </span>
                )}
                <span className="font-mono text-xs" style={{color:'var(--ink-3)'}}>
                  {new Date(viewing.created_at).toLocaleDateString(undefined,{year:'numeric',month:'short',day:'numeric'})}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  data-testid="pov-detail-prev"
                  disabled={!canPrev}
                  onClick={goPrev}
                  className="p-2 rounded-full hover:bg-[color:var(--paper)] disabled:opacity-30"
                  title="Previous (←)"
                ><CaretLeft size={16}/></button>
                <span className="text-xs font-mono" style={{color:'var(--ink-3)', minWidth: 60, textAlign:'center'}} data-testid="pov-detail-counter">
                  {viewIdx + 1} of {visible.length}
                </span>
                <button
                  type="button"
                  data-testid="pov-detail-next"
                  disabled={!canNext}
                  onClick={goNext}
                  className="p-2 rounded-full hover:bg-[color:var(--paper)] disabled:opacity-30"
                  title="Next (→)"
                ><CaretRight size={16}/></button>
                <div className="w-px h-5 mx-1" style={{background:'var(--line)'}}/>
                <button type="button" onClick={()=>setViewing(null)} className="p-2 rounded-full hover:bg-[color:var(--paper)]" title="Close (Esc)"><X size={16}/></button>
              </div>
            </div>

            <textarea
              data-testid="pov-detail-content"
              className="textarea w-full mb-3"
              rows={Math.min(14, Math.max(6, (detailDraft.content.match(/\n/g)?.length || 0) + 4))}
              value={detailDraft.content}
              onChange={(e)=>setDetailDraft(d => ({...d, content: e.target.value}))}
              onPaste={onPasteNormalize((v)=>setDetailDraft(d => ({...d, content: v})))}
              style={{ fontSize: 16, lineHeight: 1.6 }}
            />

            <div className="mb-4">
              <div className="eyebrow mb-1.5">Post type</div>
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="pov-detail-post-type">
                {POST_TYPE_OPTIONS.map(t => {
                  const selected = (detailDraft.post_type_tag || "").toLowerCase() === t.toLowerCase();
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={()=>setDetailDraft(d => ({...d, post_type_tag: selected ? "" : t}))}
                      className="chip text-xs cursor-pointer"
                      style={selected ? {background:'var(--accent)', color:'#fff', borderColor:'var(--accent)'} : postTypeStyle(t)}
                    >{t}</button>
                  );
                })}
                {detailDraft.post_type_tag && !POST_TYPE_OPTIONS.some(t => t.toLowerCase() === detailDraft.post_type_tag.toLowerCase()) && (
                  <span className="chip text-xs" style={{padding:'2px 8px', ...postTypeStyle(detailDraft.post_type_tag)}}>{detailDraft.post_type_tag}</span>
                )}
                {detailDraft.post_type_tag && (
                  <button type="button" onClick={()=>setDetailDraft(d => ({...d, post_type_tag: ""}))} className="chip text-xs cursor-pointer" style={{background:'var(--bg-elev)', color:'var(--ink-3)'}} title="Clear post type"><X size={10} weight="bold"/> Clear</button>
                )}
              </div>
            </div>

            {(viewing.tags || []).length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap mb-3">
                {viewing.tags.map(t => <span key={t} className="chip text-xs">#{t}</span>)}
              </div>
            )}
            {viewing.source && (
              <div className="text-xs mb-4" style={{color:'var(--ink-3)'}}>From: {viewing.source}</div>
            )}

            <label className="flex items-center gap-2 mb-4 cursor-pointer text-xs" style={{color:'var(--ink-2)'}}>
              <input type="checkbox" checked={detailDraft.active} onChange={(e)=>setDetailDraft(d => ({...d, active: e.target.checked}))}/>
              Let her use this in drafts proactively
            </label>

            <div className="flex items-center justify-between pt-4 border-t" style={{borderColor:'var(--line)'}}>
              <div className="flex items-center gap-1">
                {viewing.planner_post_id ? (
                  <span
                    data-testid="pov-detail-in-planner"
                    className="chip text-xs flex items-center gap-1"
                    style={{background:'var(--accent-tint)', color:'var(--accent-2)', border:'1px solid var(--accent)'}}
                  ><Check size={11} weight="bold"/> In Planner</span>
                ) : (
                  <button
                    type="button"
                    data-testid="pov-detail-to-planner"
                    onClick={() => sendToPlanner(viewing)}
                    className="btn btn-ghost"
                  ><CalendarPlus size={14}/> Send to Planner</button>
                )}
                <button
                  data-testid="pov-detail-delete"
                  onClick={async () => { const p = viewing; setViewing(null); await remove(p); }}
                  className="p-2 rounded-full hover:bg-[color:var(--paper)]"
                  style={{color:"#b83a2b"}}
                  title="Delete"
                ><Trash size={16}/></button>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={()=>setViewing(null)} className="btn btn-ghost">Close</button>
                <button
                  type="button"
                  data-testid="pov-detail-save"
                  onClick={saveDetail}
                  disabled={!detailDirty}
                  className="btn btn-accent"
                  style={!detailDirty ? { opacity: 0.5 } : {}}
                ><Check size={14}/> {detailDirty ? "Save changes" : "Saved"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-start justify-center sm:pt-16 overflow-y-auto" style={{background:'rgba(0,0,0,0.55)'}} onClick={()=>setShowAdd(false)}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={save} className="w-full sm:max-w-xl card p-5 sm:p-6 fade-up rounded-b-none sm:rounded-b-3xl" style={{maxHeight:'92vh', overflowY:'auto'}} data-testid="pov-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-2xl font-semibold">{editingId ? `Edit ${KIND_META[form.kind].short}` : "Capture"}</h3>
              <button type="button" onClick={()=>setShowAdd(false)} className="p-2 rounded-full hover:bg-[color:var(--paper)]"><X size={16}/></button>
            </div>

            <div className="mb-4">
              <div className="eyebrow mb-2">Type</div>
              <div className="grid grid-cols-2 gap-2" data-testid="pov-kind-selector">
                {["pov", "idea"].map(k => {
                  const meta = KIND_META[k];
                  const KIcon = meta.Icon;
                  const selected = form.kind === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      data-testid={`pov-kind-${k}`}
                      onClick={() => setForm({ ...form, kind: k })}
                      className={`p-3 rounded-2xl border text-left transition-all flex items-start gap-2.5 ${selected ? "border-[color:var(--accent)]" : "border-[color:var(--line)] hover:border-[color:var(--ink-4)]"}`}
                      style={selected ? { background: "color-mix(in srgb, var(--accent) 8%, transparent)" } : { background: "#fff" }}
                    >
                      <KIcon size={16} weight={selected ? "fill" : "regular"} style={{ color: selected ? "var(--accent)" : "var(--ink-3)", marginTop: 2 }} />
                      <div>
                        <div className="text-sm font-medium">{meta.label}</div>
                        <div className="text-xs" style={{ color: "var(--ink-3)" }}>
                          {k === "pov" ? "A quick opinion, hot take, or contrarian thought." : "A topic or angle you want to write a post about."}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="block mb-4"><div className="eyebrow mb-1">{form.kind === "idea" ? "The idea" : "The take"}</div>
              <textarea required data-testid="pov-content" className="textarea" rows={5} placeholder={form.kind === "idea" ? "The angle, the hook, the argument you want to make. Rough is fine." : "One or two sentences. The rawer the better, don't polish."} value={form.content} onChange={e=>setForm({...form, content:e.target.value})} onPaste={onPasteNormalize((v)=>setForm(f=>({...f, content:v})))} autoFocus/>
            </label>

            <div className="mb-4">
              <div className="eyebrow mb-1 flex items-center gap-2">
                <span>Post type</span>
                {suggesting && <span className="text-[10px] font-normal" style={{color:'var(--ink-3)'}}>· thinking…</span>}
              </div>
              {suggestion && !form.post_type_tag && (
                <div className="p-2.5 rounded-2xl mb-2 flex items-center gap-2 flex-wrap" style={{background:'var(--accent-tint)', border:'1px dashed var(--accent)'}} data-testid="pov-suggestion-banner">
                  <span className="text-[11px]" style={{color:'var(--ink-2)'}}>Suggested:</span>
                  <span className="chip text-xs" style={{padding:'2px 8px', ...postTypeStyle(suggestion.tag)}}>{suggestion.tag}</span>
                  {suggestion.reason && <span className="text-[11px]" style={{color:'var(--ink-3)'}}>{suggestion.reason}</span>}
                  <button
                    type="button"
                    data-testid="pov-suggestion-accept"
                    className="ml-auto text-[11px] font-medium rounded-full px-2.5 py-1"
                    style={{background:'var(--accent)', color:'#fff'}}
                    onClick={() => setForm({ ...form, post_type_tag: suggestion.tag })}
                  >Use this</button>
                </div>
              )}
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="pov-post-type-selector">
                {POST_TYPE_OPTIONS.map(t => {
                  const selected = (form.post_type_tag || "").toLowerCase() === t.toLowerCase();
                  return (
                    <button
                      key={t}
                      type="button"
                      data-testid={`pov-post-type-${t.toLowerCase()}`}
                      onClick={() => setForm({ ...form, post_type_tag: selected ? "" : t })}
                      className="chip text-xs cursor-pointer"
                      style={selected ? {background:'var(--accent)', color:'#fff', borderColor:'var(--accent)'} : postTypeStyle(t)}
                    >{t}</button>
                  );
                })}
                {form.post_type_tag && (
                  <button
                    type="button"
                    data-testid="pov-post-type-clear"
                    onClick={() => setForm({ ...form, post_type_tag: "" })}
                    className="chip text-xs cursor-pointer"
                    style={{background:'var(--bg-elev)', color:'var(--ink-3)'}}
                    title="Clear post type"
                  ><X size={10} weight="bold"/> Clear</button>
                )}
              </div>
              <div className="text-[11px] mt-1" style={{color:'var(--ink-3)'}}>
                We suggest a type once you've written enough. Pick a different one if you disagree.
              </div>
            </div>

            <label className="block mb-4"><div className="eyebrow mb-1">Where did this come from? (optional)</div>
              <input data-testid="pov-source" className="input" placeholder="Shower, argument with a founder, that podcast…" value={form.source} onChange={e=>setForm({...form, source:e.target.value})}/>
            </label>
            <div className="mb-4">
              <div className="eyebrow mb-1">Tags</div>
              <div className="flex flex-wrap gap-2 items-center">
                {form.tags.map(t => (
                  <span key={t} className="chip flex items-center gap-1.5">#{t}<button type="button" onClick={()=>removeTag(t)} className="hover:bg-white rounded-full"><X size={10}/></button></span>
                ))}
                <input data-testid="pov-tag-input" className="input flex-1 min-w-[120px]" placeholder="Add tag…" value={tagInput} onChange={e=>setTagInput(e.target.value)} onKeyDown={e=>{ if (e.key==='Enter'){ e.preventDefault(); addTag(); } }}/>
              </div>
            </div>
            <label className="flex items-center gap-2 mb-4 cursor-pointer">
              <input data-testid="pov-active" type="checkbox" checked={form.active} onChange={e=>setForm({...form, active:e.target.checked})}/>
              <span className="text-sm">Let her use this in drafts proactively</span>
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={()=>setShowAdd(false)} className="btn btn-ghost">Cancel</button>
              <button type="submit" disabled={saving || !form.content.trim()} data-testid="pov-save" className="btn btn-accent">{saving ? "Saving…" : (editingId ? "Update" : `Save ${KIND_META[form.kind].short}`)}</button>
            </div>
          </form>
        </div>
      )}

      {showBulk && <BulkImportModal mode="pov" onClose={()=>setShowBulk(false)} onDone={load} />}

      {/* Mobile floating action bar — thumb-reachable Capture + Import */}
      <div className="sm:hidden fixed bottom-4 right-4 flex flex-col items-end gap-2 z-40">
        <button
          data-testid="pov-mobile-import-fab"
          onClick={()=>setShowBulk(true)}
          className="rounded-full shadow-lg w-11 h-11 flex items-center justify-center"
          style={{ background:'var(--paper)', color:'var(--ink-2)', border:'1px solid var(--line)' }}
          aria-label="Bulk import"
        ><FileArrowUp size={16}/></button>
        <button
          data-testid="pov-mobile-fab"
          onClick={openAdd}
          className="rounded-full shadow-lg w-14 h-14 flex items-center justify-center"
          style={{ background:'var(--accent)', color:'#fff' }}
          aria-label="Capture new PoV or idea"
        ><Plus size={22} weight="bold"/></button>
      </div>
    </div>
  );
}
